import * as React from 'react';
import { useContext, useEffect, useState } from "react";
import { Box, Button, TextField, Typography, List, ListItem, ListItemSecondaryAction, IconButton, ListItemText, Alert, Tabs, Tab, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Table, TableBody, TableCell, TableContainer, TableRow, Paper } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ApiIcon from '@mui/icons-material/Api';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import BoltIcon from '@mui/icons-material/Bolt';
import DescriptionIcon from '@mui/icons-material/Description';
import { colorText, colorWarningBackground, styleSecondaryButton } from "../App/theme.js";
import {
    getKBShares,
    shareKBWith,
    unshareKBWith,
    getAPIKeys,
    deleteAPIKey,
    createAPIKey
} from "../API/KB/KBAPI.js";

import LocalLoader from "../Loaders/LocalLoader.js";
import { isMobile, getQueryParamValue } from "../App/utils";
import { KBName } from "../names";
import { buttonVerticalMargin } from "../App/style";
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertContext } from "../App/App";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {useConfirm} from "../Components/ConfirmContext";

const ACCESS_SUBTABS = {
    API_KEYS: 'api_keys',
    COLLABORATORS: 'collaborators', 
    ENCRYPTION: 'encryption'
};

export function AccessControl({ kbId, title, AESKey, isPublishedKB }) {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [KBShares, setKBShares] = useState([]);
    const [apiKeyName, setApiKeyName] = useState("");
    const [apiKeys, setApiKeys] = useState([]);
    const [tabIndex, setTabIndex] = useState(isPublishedKB ? ACCESS_SUBTABS.COLLABORATORS : ACCESS_SUBTABS.API_KEYS);
    const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
    const [newApiKey, setNewApiKey] = useState("");
    const [showApiKey, setShowApiKey] = useState(false);
    const [selectedDocTab, setSelectedDocTab] = useState(0);
    const showConfirm = useConfirm();
    const navigate = useNavigate();
    const location = useLocation();
    const { setSystemAlert } = useContext(AlertContext);
    const reset = () => {
        setError('');
        setSuccess('');
        setEmail('');
        setApiKeyName('');
    }

    const handleDelete = async (email) => {
        const confirmed = await showConfirm({
            title: 'Revoke Access Confirmation',
            content: `Are you sure you want to revoke access for ${email}?`
        })
        if (confirmed) {
            setLoading(true);
            await unshareKBWith(email);
            setKBShares(KBShares.filter((e) => e !== email));
            reset();
            setLoading(false);
        }
    }

    const handleDeleteAPIKey = async (keyId) => {
        const confirmed = await showConfirm({
            title: 'Delete API key Confirmation',
            content: `Are you sure you want to delete this API key?`
        })
        if (confirmed) {
            setLoading(true);
            await deleteAPIKey(keyId);
            setApiKeys(apiKeys.filter((key) => key.apiKey !== keyId));
            setLoading(false);
        }
    }

    const fetchShares = () => {
        setLoading(true);
        getKBShares().then((res) => {
            const { sharedWith } = res;
            setKBShares(sharedWith);
            setLoading(false);
        });
    }

    const fetchAPIKeys = () => {
        setLoading(true);
        getAPIKeys().then((res) => {
            setApiKeys(res);
            setLoading(false);
        });
    }

    useEffect(() => {
        fetchShares();
        fetchAPIKeys();
    }, [kbId]);

    useEffect(() => {
        // For published KBs, always force collaborators tab
        if (isPublishedKB) {
            setTabIndex(ACCESS_SUBTABS.COLLABORATORS);
            return;
        }

        const params = new URLSearchParams(location.search);
        const tab = params.get('subtab');
        if (tab) {
            // Handle both old numeric values and new string values for backwards compatibility
            if (tab === '0' || tab === 'collaborators') {
                setTabIndex(ACCESS_SUBTABS.COLLABORATORS);
            } else if (tab === '1' || tab === 'api_keys') {
                setTabIndex(ACCESS_SUBTABS.API_KEYS);
            } else if (tab === '2' || tab === 'aes_key' || tab === 'encryption') {
                setTabIndex(ACCESS_SUBTABS.ENCRYPTION);
            } else {
                setTabIndex(tab);
            }
        }
    }, [location.search, isPublishedKB]);

    useEffect(() => {
        const createAPIKeyParam = getQueryParamValue('createAPIKey');
        if (createAPIKeyParam && title) {
            setTabIndex(ACCESS_SUBTABS.API_KEYS);
            setApiKeyName(createAPIKeyParam);
            
            const createAutoAPIKey = async () => {
                try {
                    setLoading(true);
                    const { apiKey } = await createAPIKey(createAPIKeyParam);
                    setNewApiKey(apiKey);
                    setApiKeyDialogOpen(true);
                    setLoading(false);
                    setError('');
                    fetchAPIKeys();
                } catch (error) {
                    setLoading(false);
                    setError(error.message || error.toString());
                }
            };
            
            createAutoAPIKey();
        }
    }, [title]);

    if (!title) return null;

    const conversationExample = `# Start: Get financial news analysis
curl -X POST https://${kbId}.apps.openkbs.com/api \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "message": "Perform a single search for NVIDIA financial news. Based on the response, for each article, provide title, summary, link, full translation in (es, zh and de) and the sentiment (Positive, Negative, or Neutral). Return a single JSON object with a key news_analysis."
  }'

# Continue: Create comprehensive risk assessment
curl -X POST https://${kbId}.apps.openkbs.com/api \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "chatId": "CHAT_ID_FROM_RESPONSE", 
    "message": "Based on this analysis, create a risk assessment for NVIDIA stock. Include potential threats, opportunities, and a recommendation (Buy, Hold, Sell) with confidence score (1-10). Return as JSON with key risk_assessment.",
    "includeHistory": true,
    "historyLimit": 50
  }'`;

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const res = await shareKBWith(email);
            setLoading(false);
            setError('');
            reset();
            fetchShares();
        } catch (error) {
            setLoading(false);
            const errorMessage = error.response && error.response.data && error.response.data.error
                ? error.response.data.error
                : error.message || error.toString();
            if (errorMessage.startsWith('Error: User does not exist')) {
                setError(`User ${email} does not exist`);
            } else if (errorMessage.startsWith('Error: App is already share')) {
                setError(`App is already shared with ${email}`);
            } else {
                setError(errorMessage);
            }
        }
    };

    const handleCreateAPIKey = async () => {
        try {
            setLoading(true);
            const { apiKey } = await createAPIKey(apiKeyName);
            setNewApiKey(apiKey);
            setApiKeyDialogOpen(true);
            setLoading(false);
            setError('');
            reset();
            fetchAPIKeys();
        } catch (error) {
            setLoading(false);
            setError(error.message || error.toString());
        }
    };

    const handleTabChange = (event, newValue) => {
        // For published KBs, only allow collaborators tab
        if (isPublishedKB && newValue !== ACCESS_SUBTABS.COLLABORATORS) {
            return;
        }

        setTabIndex(newValue);
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('subtab', newValue);
        const newUrl = `${location.pathname}?${searchParams.toString()}`;
        navigate(newUrl);
    };

    const handleCloseDialog = () => {
        setApiKeyDialogOpen(false);
        setNewApiKey("");
        setShowApiKey(false);
    };

    const toggleShowApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    return (
        <Box sx={{ padding: 2, color: colorText }}>
            <LocalLoader open={isLoading} blocking={true} />
            <Typography variant="h6" sx={{ marginBottom: 2 }}>Manage Access for "{title}"</Typography>

            <Alert severity="info" sx={{ marginBottom: 4, marginTop: 4 }}>
                kbId: {kbId}
            </Alert>

            {/*{tabIndex === ACCESS_SUBTABS.COLLABORATORS && <Alert severity="info" sx={{ marginBottom: 4, marginTop: 4 }}>*/}
            {/*    Keep in mind that granting full access means the recipient will have the same access level as you do.*/}
            {/*    {!isMobile && ` This includes full read and write access, the ability to delete the application, modify its source code, alter or delete data, and unrestricted spending from the associated credits account.`}*/}
            {/*</Alert>}*/}

            {!isPublishedKB ? (
                <Tabs value={tabIndex} onChange={handleTabChange}>
                    <Tab label="API Keys" value={ACCESS_SUBTABS.API_KEYS} style={{ textTransform: 'none' }} />
                    <Tab label="Collaborators" value={ACCESS_SUBTABS.COLLABORATORS} style={{ textTransform: 'none' }} />
                    <Tab label="Encryption" value={ACCESS_SUBTABS.ENCRYPTION} style={{ textTransform: 'none' }} />
                </Tabs>
            ) : (
                <Tabs value={ACCESS_SUBTABS.COLLABORATORS} onChange={handleTabChange}>
                    <Tab label="Collaborators" value={ACCESS_SUBTABS.COLLABORATORS} style={{ textTransform: 'none' }} />
                </Tabs>
            )}
            <br />
            {tabIndex === ACCESS_SUBTABS.COLLABORATORS && (
                <Box>
                    <TextField
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter' && email.length > 0) { handleSubmit(); } }}
                        value={email}
                        style={{ marginTop: '32px' }}
                        autoFocus
                        margin="dense"
                        id="name"
                        label={success ? success : 'User Email Address'}
                        type="email"
                        fullWidth
                        variant="standard"
                        color={success ? "success" : ''}
                        error={error.length > 0}
                        helperText={error}
                    />
                    <Box sx={{ marginTop: buttonVerticalMargin, float: (isMobile ? undefined : "right") }}>
                        <Button
                            variant="contained"
                            fullWidth={isMobile}
                            color="secondary"
                            sx={{ marginRight: "16px", width: isMobile ? null : '220px', marginBottom: isMobile ? 2 : 0 }}
                            disabled={!email.length}
                            onClick={handleSubmit}
                        >
                            Grant Full Access
                        </Button>
                    </Box>
                    {KBShares.length > 0 && (
                        <Box sx={{ marginTop: isMobile ? '60px' : '100px' }}>
                            <Typography variant="h6">User Access List:</Typography>
                            <List style={{ marginBottom: '16px' }}>
                                {KBShares.map((email) => (
                                    <ListItem key={email}>
                                        <ListItemText primary={email} />
                                        <ListItemSecondaryAction>
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(email)}>
                                                <DeleteIcon fontSize={"small"} />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                </Box>
            )}
            {tabIndex === ACCESS_SUBTABS.API_KEYS && (
                <Box>
                    {/* Quick Actions Header */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        mb: 4,
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center'
                    }}>
                        <TextField
                            onChange={(e) => setApiKeyName(e.target.value)}
                            onKeyPress={(e) => { if (e.key === 'Enter' && apiKeyName.length > 0) { handleCreateAPIKey() } }}
                            value={apiKeyName}
                            autoFocus
                            placeholder="e.g. Production App, Mobile Client"
                            label="API Key Name"
                            variant="outlined"
                            size="small"
                            sx={{ flex: 1, minWidth: '200px' }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!apiKeyName.length}
                            onClick={handleCreateAPIKey}
                            sx={{ 
                                minWidth: '140px',
                                height: '40px',
                                fontWeight: 600
                            }}
                        >
                            Create Key
                        </Button>
                    </Box>

                    {/* Main Content Grid */}
                    <Box sx={{ 
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '350px 1fr',
                        gap: 4,
                        minHeight: '500px'
                    }}>
                        {/* Left Sidebar - API Keys Management */}
                        <Box sx={{ 
                            backgroundColor: 'rgba(0,0,0,0.02)', 
                            padding: 3, 
                            borderRadius: 2,
                            border: '1px solid rgba(0,0,0,0.1)'
                        }}>
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ApiIcon /> Your API Keys
                                {apiKeys.length > 0 && (
                                    <Box component="span" sx={{ 
                                        fontSize: '12px', 
                                        backgroundColor: 'primary.main', 
                                        color: 'white', 
                                        px: 1, 
                                        py: 0.5, 
                                        borderRadius: '10px' 
                                    }}>
                                        {apiKeys.length}
                                    </Box>
                                )}
                            </Typography>
                            
                            {apiKeys.length === 0 ? (
                                <Box sx={{ 
                                    textAlign: 'center', 
                                    py: 4, 
                                    color: 'text.secondary',
                                    fontStyle: 'italic'
                                }}>
                                    No API keys yet. Create your first one above.
                                </Box>
                            ) : (
                                <Box>
                                    {apiKeys.map((key) => (
                                        <Box key={key.apiKey} sx={{ 
                                            mb: 2, 
                                            p: 2, 
                                            backgroundColor: 'white', 
                                            borderRadius: 1,
                                            border: '1px solid rgba(0,0,0,0.08)',
                                            '&:hover': { 
                                                backgroundColor: 'rgba(25,118,210,0.02)',
                                                borderColor: 'primary.main'
                                            }
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                        {key.name}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        Created: {new Date(key.createdAt).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleDeleteAPIKey(key.apiKey)}
                                                    sx={{ color: 'error.main' }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        {/* Right Main Area - Documentation with Tabs */}
                        <Box>
                            <Tabs value={selectedDocTab} onChange={(e, v) => setSelectedDocTab(v)} sx={{ mb: 3 }}>
                                <Tab 
                                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><RocketLaunchIcon fontSize="small" />Quick Start</Box>} 
                                    value={0} 
                                    style={{ textTransform: 'none' }} 
                                />
                                <Tab 
                                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><BoltIcon fontSize="small" />Advanced</Box>} 
                                    value={1} 
                                    style={{ textTransform: 'none' }} 
                                />
                                <Tab 
                                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><DescriptionIcon fontSize="small" />Reference</Box>} 
                                    value={2} 
                                    style={{ textTransform: 'none' }} 
                                />
                            </Tabs>

                            {/* Quick Start Tab */}
                            {selectedDocTab === 0 && (
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <RocketLaunchIcon /> Basic API Call
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={6}
                                        variant="outlined"
                                        value={`curl -X POST https://${kbId}.apps.openkbs.com/api \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "message": "Your task or request here"
  }'`}
                                        InputProps={{ 
                                            readOnly: true,
                                            style: { 
                                                fontFamily: 'Monaco, "Courier New", monospace', 
                                                fontSize: '13px', 
                                                backgroundColor: '#f8f9fa' 
                                            }
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                        <CopyToClipboard text={`curl -X POST https://${kbId}.apps.openkbs.com/api -H "Content-Type: application/json" -H "Authorization: Bearer YOUR_API_KEY" -d '{"message": "Your task or request here"}'`}>
                                            <Button 
                                                variant="outlined"
                                                size="small"
                                                onClick={() => setSystemAlert({ msg: '✅ Basic request copied!', type: 'success', duration: 2000 })}
                                            >
                                                Copy cURL
                                            </Button>
                                        </CopyToClipboard>
                                    </Box>
                                </Box>
                            )}

                            {/* Advanced Tab */}
                            {selectedDocTab === 1 && (
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <BoltIcon /> Conversation with Memory
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={14}
                                        variant="outlined"
                                        value={conversationExample}
                                        InputProps={{ 
                                            readOnly: true,
                                            style: { 
                                                fontFamily: 'Monaco, "Courier New", monospace', 
                                                fontSize: '12px', 
                                                backgroundColor: '#f8f9fa' 
                                            }
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                        <CopyToClipboard text={conversationExample}>
                                            <Button 
                                                variant="outlined"
                                                size="small"
                                                onClick={() => setSystemAlert({ msg: '✅ Conversation flow copied!', type: 'success', duration: 2000 })}
                                            >
                                                Copy Advanced
                                            </Button>
                                        </CopyToClipboard>
                                    </Box>
                                </Box>
                            )}

                            {/* Reference Tab */}
                            {selectedDocTab === 2 && (
                                <Box>
                                    <Typography variant="h6" sx={{ mb: 3, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <DescriptionIcon /> Parameters Reference
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', width: '160px', backgroundColor: '#f5f5f5' }}>message</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', color: 'error.main', width: '120px' }}>required</TableCell>
                                                    <TableCell>Your task or request</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>chatId</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>optional</TableCell>
                                                    <TableCell>Continue existing conversation</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>includeHistory</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>optional</TableCell>
                                                    <TableCell>Include previous chat messages</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>historyLimit</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>optional</TableCell>
                                                    <TableCell>Max history messages (1-100)</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>includeAllMessages</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>optional</TableCell>
                                                    <TableCell>Include all raw messages (default: clean only)</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        <strong>Pro Tip:</strong> Use <code>historyLimit</code> to control context size and response speed. 
                                        Lower values = faster responses, higher values = more context awareness.
                                    </Alert>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}

            {tabIndex === ACCESS_SUBTABS.ENCRYPTION && (
                <Box>
                    <Alert severity="warning" sx={{ marginBottom: 4, marginTop: 4 }}>
                        This is your encryption key. Keep it secure and don't share it with anyone you don't trust completely.
                        Anyone with this key can decrypt all data in this application.
                    </Alert>

                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 4 }}>
                        <TextField
                            fullWidth
                            label="Encryption Key"
                            variant="outlined"
                            value={AESKey || ''}
                            type={showApiKey ? "text" : "password"}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <IconButton
                                        onClick={toggleShowApiKey}
                                        edge="end"
                                        sx={{ mr: 1 }}
                                    >
                                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                ),
                            }}
                        />
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <CopyToClipboard text={AESKey || ''}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setSystemAlert({ msg: 'Encryption Key Copied', type: 'success', duration: 1500 })}
                                sx={{ width: isMobile ? '100%' : '200px' }}
                            >
                                Copy Encryption Key
                            </Button>
                        </CopyToClipboard>
                    </Box>
                </Box>
            )}

            <Dialog open={apiKeyDialogOpen} onClose={handleCloseDialog}>
                <DialogTitle>API Key Created</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Alert severity="success" sx={{ marginBottom: 4, marginTop: 4 }}>
                            Your new API key has been created. Please copy and save it securely. This key will not be shown again.
                        </Alert>

                    </DialogContentText>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="apiKey"
                            label="API Key"
                            type={showApiKey ? "text" : "password"}
                            fullWidth
                            variant="standard"
                            value={newApiKey}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                        <IconButton onClick={toggleShowApiKey} edge="end">
                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <CopyToClipboard text={newApiKey}>
                        <Button variant="contained" sx={{ mr: 1, width: 120 }}
                                onClick={() => setSystemAlert({ msg: 'API Key Copied', type: 'success', duration: 1500 })}>
                            Copy API Key
                        </Button>
                    </CopyToClipboard>
                    <Button variant="outlined" onClick={handleCloseDialog} color="primary" sx={{ mr: 1, width: 120 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}